import { ref, computed } from 'vue'
import axios from 'axios'

/**
 * Composable para gerenciar lembretes automáticos de notificação
 */
export function useNotificationReminders() {
  const lembretes = ref([])
  const loading = ref(false)
  const error = ref(null)

  // Estado do modal
  const mostrarModal = ref(false)
  const modoEdicao = ref(false)
  const lembreteSelecionado = ref(null)

  // Passo do wizard (4 passos)
  const passoAtual = ref(1)

  // Configuração dos tipos de lembrete
  const tiposConfig = {
    aniversario: {
      label: 'Aniversário',
      icon: 'fas fa-birthday-cake',
      cor: '#e91e63',
      corBg: '#fce4ec',
      descricao: 'Envia automaticamente no dia do aniversário do paciente',
    },
    boas_vindas: {
      label: 'Boas-vindas',
      icon: 'fas fa-hand-sparkles',
      cor: '#4caf50',
      corBg: '#e8f5e9',
      descricao: 'Envia X dias após o cadastro do paciente',
    },
    inatividade: {
      label: 'Inatividade',
      icon: 'fas fa-user-clock',
      cor: '#ff9800',
      corBg: '#fff3e0',
      descricao: 'Envia após X dias sem receber notificações',
    },
    recorrente: {
      label: 'Recorrente',
      icon: 'fas fa-sync',
      cor: '#2196f3',
      corBg: '#e3f2fd',
      descricao: 'Envia em intervalos regulares (diário, semanal, mensal)',
    },
  }

  // Opções de frequência para lembretes recorrentes
  const frequenciasConfig = {
    diario: { label: 'Diariamente', descricao: 'Todo dia' },
    semanal: { label: 'Semanalmente', descricao: 'Uma vez por semana' },
    mensal: { label: 'Mensalmente', descricao: 'Uma vez por mês' },
  }

  // Dias da semana
  const diasSemana = [
    { value: 1, label: 'Segunda-feira' },
    { value: 2, label: 'Terça-feira' },
    { value: 3, label: 'Quarta-feira' },
    { value: 4, label: 'Quinta-feira' },
    { value: 5, label: 'Sexta-feira' },
    { value: 6, label: 'Sábado' },
    { value: 7, label: 'Domingo' },
  ]

  /**
   * Carregar lista de lembretes
   */
  async function carregarLembretes(filtros = {}) {
    loading.value = true
    error.value = null

    try {
      const params = new URLSearchParams()
      if (filtros.tipo) params.append('tipo', filtros.tipo)
      if (filtros.canal) params.append('canal', filtros.canal)
      if (filtros.ativo !== undefined) params.append('ativo', filtros.ativo)

      const response = await axios.get(`/api/notificacoes/lembretes?${params}`)
      lembretes.value = response.data
      return lembretes.value
    } catch (err) {
      error.value = err.response?.data?.message || 'Erro ao carregar lembretes'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Carregar lembrete específico com detalhes
   */
  async function carregarLembrete(id) {
    loading.value = true
    try {
      const response = await axios.get(`/api/notificacoes/lembretes/${id}`)
      return response.data
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Criar novo lembrete
   */
  async function criarLembrete(dados) {
    loading.value = true
    try {
      const response = await axios.post('/api/notificacoes/lembretes', dados)
      lembretes.value.unshift(response.data.lembrete)
      return response.data
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Atualizar lembrete existente
   */
  async function atualizarLembrete(id, dados) {
    loading.value = true
    try {
      const response = await axios.put(`/api/notificacoes/lembretes/${id}`, dados)
      const index = lembretes.value.findIndex((l) => l.id === id)
      if (index !== -1) {
        lembretes.value[index] = response.data.lembrete
      }
      return response.data
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Excluir lembrete
   */
  async function excluirLembrete(id) {
    loading.value = true
    try {
      await axios.delete(`/api/notificacoes/lembretes/${id}`)
      lembretes.value = lembretes.value.filter((l) => l.id !== id)
      return true
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Alternar status ativo/inativo
   */
  async function toggleAtivo(id) {
    try {
      const response = await axios.post(`/api/notificacoes/lembretes/${id}/toggle`)
      const index = lembretes.value.findIndex((l) => l.id === id)
      if (index !== -1) {
        lembretes.value[index] = response.data.lembrete
      }
      return response.data
    } catch (err) {
      throw err
    }
  }

  /**
   * Contar destinatários (preview)
   */
  async function contarDestinatarios(dados) {
    try {
      const response = await axios.post('/api/notificacoes/lembretes/contar-destinatarios', dados)
      return response.data
    } catch (err) {
      throw err
    }
  }

  /**
   * Carregar logs de um lembrete
   */
  async function carregarLogs(id, filtros = {}) {
    try {
      const params = new URLSearchParams()
      if (filtros.status) params.append('status', filtros.status)
      if (filtros.data) params.append('data', filtros.data)
      if (filtros.page) params.append('page', filtros.page)

      const response = await axios.get(`/api/notificacoes/lembretes/${id}/logs?${params}`)
      return response.data
    } catch (err) {
      throw err
    }
  }

  /**
   * Carregar estatísticas gerais
   */
  async function carregarStats() {
    try {
      const response = await axios.get('/api/notificacoes/lembretes-stats')
      return response.data
    } catch (err) {
      throw err
    }
  }

  // Funções do modal
  function abrirModalCriar() {
    lembreteSelecionado.value = {
      nome: '',
      descricao: '',
      tipo: 'aniversario',
      template_id: null,
      canal: 'email',
      filtros: {
        uf: [],
        sistema_inscrito: [],
        sexo: [],
        idade_min: null,
        idade_max: null,
      },
      configuracao: {
        hora_envio: '09:00',
        dias_apos_cadastro: 7,
        dias_sem_interacao: 30,
        frequencia: 'semanal',
        dia_semana: 1,
        dia_mes: 1,
      },
      ativo: true,
    }
    modoEdicao.value = false
    passoAtual.value = 1
    mostrarModal.value = true
  }

  function abrirModalEditar(lembrete) {
    lembreteSelecionado.value = {
      ...lembrete,
      filtros: lembrete.filtros || {
        uf: [],
        sistema_inscrito: [],
        sexo: [],
        idade_min: null,
        idade_max: null,
      },
      configuracao: {
        hora_envio: '09:00',
        dias_apos_cadastro: 7,
        dias_sem_interacao: 30,
        frequencia: 'semanal',
        dia_semana: 1,
        dia_mes: 1,
        ...lembrete.configuracao,
      },
    }
    modoEdicao.value = true
    passoAtual.value = 1
    mostrarModal.value = true
  }

  function fecharModal() {
    mostrarModal.value = false
    lembreteSelecionado.value = null
    modoEdicao.value = false
    passoAtual.value = 1
  }

  function avancarPasso() {
    if (passoAtual.value < 4) {
      passoAtual.value++
    }
  }

  function voltarPasso() {
    if (passoAtual.value > 1) {
      passoAtual.value--
    }
  }

  // Computed
  const lembretesAtivos = computed(() => lembretes.value.filter((l) => l.ativo))

  const lembretesInativos = computed(() => lembretes.value.filter((l) => !l.ativo))

  const lembretesPorTipo = computed(() => {
    return (tipo) => lembretes.value.filter((l) => l.tipo === tipo)
  })

  /**
   * Retorna descrição legível da configuração
   */
  function getDescricaoConfiguracao(lembrete) {
    const config = lembrete.configuracao || {}
    const hora = config.hora_envio || '09:00'

    switch (lembrete.tipo) {
      case 'aniversario':
        return `Envia às ${hora} no dia do aniversário`

      case 'boas_vindas': {
        const dias = config.dias_apos_cadastro || 7
        return `Envia às ${hora}, ${dias} dia(s) após cadastro`
      }

      case 'inatividade': {
        const dias = config.dias_sem_interacao || 30
        return `Envia às ${hora} após ${dias} dia(s) sem interação`
      }

      case 'recorrente': {
        const freq = config.frequencia || 'semanal'
        if (freq === 'diario') return `Diariamente às ${hora}`
        if (freq === 'semanal') {
          const dia = diasSemana.find((d) => d.value === config.dia_semana)?.label || 'Segunda'
          return `${dia} às ${hora}`
        }
        if (freq === 'mensal') {
          return `Dia ${config.dia_mes || 1} de cada mês às ${hora}`
        }
        return `${freq} às ${hora}`
      }

      default:
        return 'Configuração desconhecida'
    }
  }

  return {
    // State
    lembretes,
    loading,
    error,
    mostrarModal,
    modoEdicao,
    lembreteSelecionado,
    passoAtual,
    tiposConfig,
    frequenciasConfig,
    diasSemana,

    // Computed
    lembretesAtivos,
    lembretesInativos,
    lembretesPorTipo,

    // Actions
    carregarLembretes,
    carregarLembrete,
    criarLembrete,
    atualizarLembrete,
    excluirLembrete,
    toggleAtivo,
    contarDestinatarios,
    carregarLogs,
    carregarStats,

    // Modal
    abrirModalCriar,
    abrirModalEditar,
    fecharModal,
    avancarPasso,
    voltarPasso,

    // Helpers
    getDescricaoConfiguracao,
  }
}
